import React, { useEffect, useRef } from "react";
import _Button from "../../../components/generic/_Button";
import PropTypes from "prop-types";
import { Alert, Spinner } from "react-bootstrap";
import Icon from "../../../components/Icon";
import StepOne from "./steps/StepOne";
import StepTwo from "./steps/StepTwo";
import StepThree from "./steps/StepThree";
import StepFour from "./steps/StepFour";
//import StepFive from "./steps/StepFive";
import { ProgressBar } from "../../../components/ui/ProgressBar";
import { useFileInput, useFieldErrors } from "../hooks";
import { BsFiletypeCsv } from "react-icons/bs";
import { classNames } from "../../../utils/helpers";

const FileUpload = ({ formik, fileUploadState, stepData }) => {
  const stepThreeRef = useRef(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const {
    ignoreHeaders,
    setIgnoreHeaders,
    newData,
    setNewData,
    reset: resetStepData,
  } = stepData;

  const { ref: fileRef, open } = useFileInput();
  const {
    upload,
    progress,
    data: previousData,
    headers: headersRow,
    fileInformation,
    reset,
    initProcess,
    invalidData,
    summary,
    validData,
    processing,
  } = fileUploadState;
  const { fieldErrors, setFieldErrors } = useFieldErrors();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    upload(file);
  };

  useEffect(() => {
    formik.setFieldValue("data", newData);
  }, [newData]);

  const renderSteps = () => {
    switch (formik.values.step) {
      case 1:
        return <StepOne data={previousData} summary={summary} />;
      case 2:
        return (
          <StepTwo
            data={headersRow}
            selectedHeaders={ignoreHeaders}
            setSelectedHeaders={setIgnoreHeaders}
          />
        );
      case 3:
        return (
          <StepThree
            ref={stepThreeRef}
            selectedHeaders={ignoreHeaders}
            data={previousData}
            validData={validData}
            invalidData={invalidData}
            newData={newData}
            setNewData={setNewData}
            summary={summary}
            formik={formik}
            fileInformation={fileInformation}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
          />
        );
      case 4:
        return (
          <StepFour
            data={newData}
            formik={formik}
            summary={summary}
            fieldErrors={fieldErrors}
            fileInformation={fileInformation}
          />
        );
      default:
        return null;
    }
  };

  const isEmpty = !previousData || previousData.length === 0;

  const renderEmptyState = () => {
    if (initProcess) {
      return (
        <ProgressBar
          percentage={progress}
          showInformation
          information="Subiendo archivo"
        />
      );
    }

    return (
      <>
        <div
          className="w-full rounded-lg flex items-center justify-center gap-2 p-4 cursor-pointer border-2 border-gray-300 border-dashed border-border hover:bg-gray-100"
          onClick={open}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleFile}
          />
          <Icon icon="upload" />
          Subir Archivo
        </div>

        <Alert variant="secondary" className="Notice">
          <b>Nota:</b> La primer columna debe ser el número de teléfono. El
          archivo debe ser <b>.csv</b>.
        </Alert>
      </>
    );
  };

  const renderWithData = () => (
    <>
      <div className="flex justify-between border px-4 py-2 rounded-lg">
        <div className="flex justify-start items-end">
          <BsFiletypeCsv size={46} />
          <span className="text-sm text-gray-600">{fileInformation.name}</span>
        </div>

        <button
          className="text-sm text-gray-600 bg-gray-100 hover:bg-gray-400 w-10 h-10 rounded-xl flex items-center justify-center border-none"
          onClick={() => {
            resetStepData();
            reset();
          }}
        >
          <Icon icon="close" />
        </button>
      </div>

      <div className="border rounded-lg p-3 my-1">{renderSteps()}</div>

      <div className="flex justify-between">
        <_Button
          onClick={() => formik.setFieldValue("step", formik.values.step - 1)}
          style="none"
          disabled={formik.values.step === 1}
          icon="arrow_back"
          title="Atrás"
        />

        <_Button
          onClick={async () => {
            if (formik.values.step === 3) {
              setIsLoading(true);
              try {
                await stepThreeRef.current.handleUpload();
                formik.setFieldValue("isOkFileToSave", true);
                formik.setFieldValue("step", 4);
              } catch (error) {
                console.log(error);
                formik.setFieldValue("isOkFileToSave", false);
              } finally {
                setIsLoading(false);
              }

              return;
            }

            formik.setFieldValue("step", formik.values.step + 1);
          }}
          style="primary"
          disabled={formik.values.step === 4}
          icon="arrow_forward"
          title="Siguiente"
          loading={isLoading}
          className={classNames({
            hidden: formik.values.step === 4,
          })}
        />
      </div>
    </>
  );

  return (
    <div>
      <div className="flex justify-between flex-col bg-white py-3 rounded-lg">
        {isEmpty ? renderEmptyState() : renderWithData()}
        {processing && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg gap-2">
            <Spinner animation="border" variant="primary" />
            <span className="animate-pulse text-lg"> Procesando...</span>
          </div>
        )}
      </div>
    </div>
  );
};

FileUpload.propTypes = {
  formik: PropTypes.object,
  fileUploadState: PropTypes.object,
  stepData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
};

export default FileUpload;
